import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ImagePlus, X, Trash2 } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export const VehiclePhotosStrip = ({ photos = [], testid = "vehicle-photos-strip" }) => (
  photos.length ? (
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid={testid}>
      {photos.map((p) => (
        <a key={p.id} href={fileUrl(p.id)} target="_blank" rel="noreferrer" className="shrink-0">
          <img src={fileUrl(p.id)} alt={p.name} className="h-20 w-28 rounded-lg border border-slate-200 object-cover" /></a>))}
    </div>) : null
);

export const FleetPhotosDialog = ({ fleet, onClose, onSaved }) => {
  const [photos, setPhotos] = useState(fleet?.photos || []);
  const [busy, setBusy] = useState(false);
  const inp = useRef(null);
  if (!fleet) return null;
  const save = async (next) => {
    setPhotos(next);
    try { await api.rentalFleetPhotos(fleet.id, next); onSaved(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
  };
  const add = async (files) => {
    setBusy(true);
    const next = [...photos];
    for (const f of [...files].slice(0, 8 - photos.length)) {
      try { const r = await api.uploadFile(f); next.push({ id: r.id || r.attachment_id, name: f.name }); } catch { toast.error(`تعذّر رفع ${f.name}`); }
    }
    await save(next);
    setBusy(false);
    if (inp.current) inp.current.value = "";
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg font-body" dir="rtl" data-testid="rental-photos-dialog">
        <DialogHeader><DialogTitle>صور {fleet.identifier}</DialogTitle><DialogDescription>حتى 8 صور — تظهر في العقد وعند التسليم والاستلام.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          {photos.map((p, i) => (
            <div key={p.id} className="group relative" data-testid={`rental-photo-${i}`}>
              <img src={fileUrl(p.id)} alt={p.name} className="h-24 w-full rounded-lg border border-slate-200 object-cover" />
              <button onClick={() => save(photos.filter((x) => x.id !== p.id))} className="absolute left-1 top-1 rounded-full bg-black/60 p-1 text-white" data-testid={`rental-photo-del-${i}`}><X className="h-3 w-3" /></button>
            </div>))}
          {photos.length < 8 && (
            <button onClick={() => inp.current?.click()} disabled={busy} className="grid h-24 place-items-center rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-blue-400 hover:text-blue-600" data-testid="rental-photo-add">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-6 w-6" />}</button>)}
        </div>
        <input ref={inp} type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files?.length && add(e.target.files)} data-testid="rental-photo-input" />
      </DialogContent>
    </Dialog>
  );
};

const localDay = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const CATS = ["صيانة", "قطع غيار", "تأمين", "تجديد استمارة", "فحص دوري", "غسيل", "أخرى"];

export const FleetExpensesDialog = ({ fleet, onClose, onSaved }) => {
  const [list, setList] = useState(fleet?.expenses || []);
  const [f, setF] = useState({ amount: "", date: localDay(), category: "صيانة", note: "" });
  const [busy, setBusy] = useState(false);
  if (!fleet) return null;
  const add = async () => {
    setBusy(true);
    try { const e = await api.rentalFleetExpenseAdd(fleet.id, { ...f, amount: Number(f.amount) || 0 }); setList((l) => [...l, e]); setF({ ...f, amount: "", note: "" }); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  const del = async (id) => {
    try { await api.rentalFleetExpenseDel(fleet.id, id); setList((l) => l.filter((x) => x.id !== id)); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };
  const total = list.reduce((s, x) => s + Number(x.amount || 0), 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="rental-expenses-dialog">
        <DialogHeader><DialogTitle>مصاريف {fleet.identifier}</DialogTitle><DialogDescription>تدخل في تقرير ربح المركبة الشهري.{!fleet.standalone && " مصاريف قسم الصيانة تُحسب تلقائياً."}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" dir="ltr" placeholder="المبلغ" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="rental-exp-amount" />
          <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="rental-exp-date" />
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="rental-exp-cat">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
          <Input placeholder="ملاحظة" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="rental-exp-note" />
        </div>
        <Button onClick={add} disabled={busy || !(Number(f.amount) > 0)} className="bg-blue-600 hover:bg-blue-700" data-testid="rental-exp-add">{busy && <Loader2 className="h-4 w-4 animate-spin" />} إضافة مصروف</Button>
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="rental-exp-list">
          {[...list].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((x) => (
            <div key={x.id} className="flex items-center gap-2 p-2 text-sm">
              <span className="w-24 shrink-0 text-xs text-slate-500">{x.date}</span><span className="rounded bg-slate-100 px-1.5 text-xs">{x.category}</span>
              <span className="min-w-0 flex-1 truncate text-xs text-slate-500">{x.note}</span><b>{Number(x.amount).toLocaleString("en-US")}</b>
              <button onClick={() => del(x.id)} data-testid={`rental-exp-del-${x.id}`} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>))}
          {!list.length && <p className="p-4 text-center text-xs text-slate-400">لا توجد مصاريف</p>}
        </div>
        {list.length > 0 && <p className="text-sm">الإجمالي: <b data-testid="rental-exp-total">{total.toLocaleString("en-US")}</b> ريال</p>}
      </DialogContent>
    </Dialog>
  );
};
